import { useState } from 'react';
import { UserAddress, UserAddressCreateRequest } from '../../../clients/quipli-service/user/user';
import { Field } from '../../../components/field/field';
import { Card } from '../../../components/card/card';
import { Checkbox } from '../../../components/checkbox/checkbox';
import classNames from 'classnames';
import { Divider } from '../../../components/divider/divider';
import { Button, EcommerceButton } from '../../../components/form/button';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import Icon from '../../../components/icons/icon';
import { ecommerceToast } from '../../../util/toasts';
import { DeliveryLocation } from '../../../clients/quipli-service/delivery/delivery';
import { SelectForm } from '../../../components/select-form/select-form';
import { useCustomer } from '../../../hooks/use-customer-context';
import { Phone } from '../../../components/field/phone';
import { PlacesAutocomplete } from '../../../components/field/places-autocomplete';

export const AccountAddressForm = ({
  address,
  type,
  onConfirm,
  onClose,
  enableDuplicate = true,
  isLocationsModuleEnabled,
  deliveryLocations,
}: {
  address?: UserAddress;
  type: 'billing' | 'delivery';
  onConfirm?: () => void;
  onClose?: () => void;
  enableDuplicate?: boolean;
  isLocationsModuleEnabled: boolean;
  deliveryLocations: DeliveryLocation[];
}) => {
  const customer = useCustomer();
  const { createUserAddress, updateUserAddress } = useCustomerAPI();

  const [isInProgress, setIsInProgress] = useState<boolean>(false);

  const [name, setName] = useState<string | undefined>(customer?.name || address?.name);
  const [phone, setPhone] = useState<string | undefined>(address?.phone);
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [company, setCompany] = useState<string | undefined>(address?.companyName);
  const [address1, setAddress1] = useState<string | undefined>(address?.address1);
  const [address2, setAddress2] = useState<string | undefined>(address?.address2);
  const [city, setCity] = useState<string | undefined>(address?.city);
  const [state, setState] = useState<string | undefined>(address?.state);
  const [zip, setZip] = useState<string | undefined>(address?.zip);
  const [country, setCountry] = useState<string | undefined>(address?.country);
  const [isGoogleGenerated, setIsGoogleGenerated] = useState<boolean>(address?.isGoogleGenerated ?? false);
  const [duplicate, setDuplicate] = useState<boolean>(false);
  const [deliveryLocation, setDeliveryLocation] = useState<DeliveryLocation | undefined>(
    address?.address1 != '-1'
      ? deliveryLocations.find((dl) => dl.address == address?.address1)
      : { id: 0, address: 'Other' },
  );
  const [customDeliveryLocation, setCustomDeliveryLocation] = useState<string>(address?.customDeliveryLocation || '');

  const isSubmitDisabled =
    !name ||
    !address1 ||
    (isLocationsModuleEnabled &&
      type === 'delivery' &&
      (!deliveryLocation || (deliveryLocation.id === 0 && !customDeliveryLocation))) ||
    (!isLocationsModuleEnabled && (!address1 || !city || !state || !zip)) ||
    isInProgress;

  // Callbacks
  const submitUserAddress = async () => {
    if (isSubmitDisabled || !customer) return;

    if (!phoneIsValid) {
      ecommerceToast('Please enter a valid phone number', 'error');
      return;
    }

    setIsInProgress(true);

    try {
      const request: Omit<UserAddressCreateRequest, 'userId'> = {
        name,
        phone,
        companyName: company,
        address1,
        address2: address2 || '',
        city: city || '',
        state: state || '',
        country: country || '',
        zip: zip || '',
        isGoogleGenerated: isGoogleGenerated,
        type,
        customDeliveryLocation: deliveryLocation?.id === 0 ? customDeliveryLocation : undefined,
        isDeleted: false,
      };

      // Create/update address
      address ? await updateUserAddress(address.id, request) : await createUserAddress(request);

      // Duplicate address
      duplicate && (await createUserAddress({ ...request, type: type === 'billing' ? 'delivery' : 'billing' }));

      onConfirm?.();
    } catch (error: any) {
      console.log(error.message);
      ecommerceToast(error.message.msg || error.message || 'An error occurred', 'error');
    } finally {
      setIsInProgress(false);
    }
  };

  const close = () => {
    // Don't close modal if there is a request in progress
    if (isInProgress) return;

    onClose?.();
  };

  return (
    <>
      <div className="py-4 px-3">
        <Card className="max-w-md bg-grey-25 !rounded-lg p-3 flex flex-col gap-2">
          <Field
            type="text"
            label="Full name"
            labelType="text-xs"
            labelWeight="semi-bold"
            value={name}
            onChange={setName}
            isRequired
            dataCy="address_full_name"
          />
          <Phone
            label="Phone number"
            labelType="text-xs"
            labelWeight="semi-bold"
            isRequired
            value={phone}
            onChange={setPhone}
            onChangeValidity={setPhoneIsValid}
            dataCy="address_phone_number"
          />
          <Field
            type="text"
            label="Company name"
            labelType="text-xs"
            labelWeight="semi-bold"
            value={company}
            onChange={setCompany}
            dataCy="address_company_name"
          />

          {isLocationsModuleEnabled && type === 'delivery' ? (
            <>
              <SelectForm
                options={[...deliveryLocations, { id: 0, address: 'Other' }]}
                value={deliveryLocation}
                getOptionLabel={(option) => option.address}
                getOptionValue={(option) => `${option.id}`}
                onChange={(option) => {
                  if (!option) return;

                  setDeliveryLocation(option);
                  setCustomDeliveryLocation('');
                  setAddress1(option.id === 0 ? '-1' : option.address);
                }}
                label="Delivery Location"
                labelType="text-xs"
                labelWeight="semi-bold"
                isRequired
              />

              {deliveryLocation?.id === 0 && (
                <Field
                  type="text"
                  label="Delivery Location Custom"
                  labelType="text-xs"
                  labelWeight="semi-bold"
                  isRequired
                  value={customDeliveryLocation}
                  onChange={setCustomDeliveryLocation}
                />
              )}
            </>
          ) : (
            <>
              <div className="flex gap-2 items-end">
                <PlacesAutocomplete
                  label="Address line 1"
                  labelType="text-xs"
                  labelWeight="semi-bold"
                  value={address1}
                  dataCy="google_address"
                  onChange={setAddress1}
                  onPredictionSelected={(generatedAddress) => {
                    setZip(generatedAddress.postcode !== '' ? generatedAddress.postcode : '-');
                    setCity(generatedAddress.city !== '' ? generatedAddress.city : '-');
                    setState(generatedAddress.state !== '' ? generatedAddress.state : '-');
                    setCountry(generatedAddress.country !== '' ? generatedAddress.country : '-');

                    setIsGoogleGenerated(true);
                  }}
                  autoComplete="off"
                  className="grow"
                  apiType="customer"
                  isRequired
                />

                {isGoogleGenerated && (
                  <Button
                    className={classNames('icon-button', {
                      'bg-grey-25': isGoogleGenerated,
                    })}
                    onClick={() => setIsGoogleGenerated(false)}
                    title="Edit address manually"
                  >
                    <Icon icon="Edit" stroke="var(--grey-400)" />
                  </Button>
                )}
              </div>

              {!isGoogleGenerated && (
                <>
                  <Field
                    type="text"
                    label="Address line 2"
                    labelType="text-xs"
                    labelWeight="semi-bold"
                    value={address2}
                    onChange={setAddress2}
                  />
                  <Field
                    type="text"
                    label="State/Province name"
                    labelType="text-xs"
                    labelWeight="semi-bold"
                    value={state}
                    onChange={setState}
                    disabled={isGoogleGenerated}
                    isRequired
                    dataCy="address_state"
                  />

                  <Field
                    type="text"
                    label="City name"
                    labelType="text-xs"
                    labelWeight="semi-bold"
                    value={city}
                    onChange={setCity}
                    disabled={isGoogleGenerated}
                    isRequired
                    dataCy="address_city"
                  />

                  <Field
                    type="text"
                    label="Postcode"
                    labelType="text-xs"
                    labelWeight="semi-bold"
                    value={zip}
                    onChange={setZip}
                    disabled={isGoogleGenerated}
                    isRequired
                    dataCy="address_postcode"
                  />
                </>
              )}
            </>
          )}

          {enableDuplicate && !isLocationsModuleEnabled && (
            <div className="mt-2">
              <Checkbox
                type="ecommercePrimary"
                label={`Duplicate this as ${type === 'billing' ? 'delivery' : 'billing'}  address`}
                isChecked={duplicate}
                onChange={setDuplicate}
                dataCy="duplicate_address"
              />
            </div>
          )}
        </Card>
      </div>

      <Divider />

      <div className="flex justify-start gap-2 p-3">
        <EcommerceButton buttonStyle="tertiary" onClick={close}>
          Cancel
        </EcommerceButton>
        <EcommerceButton
          buttonStyle="primary"
          disabled={isSubmitDisabled}
          onClick={submitUserAddress}
          dataCy="address_save"
        >
          Save
        </EcommerceButton>
      </div>
    </>
  );
};
